/**
 * ClassSession type definitions matching backend schemas exactly.
 * These types ensure type safety and consistency with the API.
 */
import { z } from "zod"

// Base classSession properties matching ClassSessionBase schema
export interface ClassSessionBase {
  class_template_id: string
  session_id: string | null
  instructor_id: string | null
  session_date: string
  session_start: string
  session_end: string
  status:
    | "scheduled"
    | "in_progress"
    | "completed"
    | "cancelled"
    | "rescheduled"
    | null
  cancellation_reason: string | null
  location_override: string | null
  max_students_override: number | null
  notes: string | null
}

// Create classSession data matching ClassSessionCreate schema
export interface ClassSessionCreate extends ClassSessionBase {}

// Update classSession data matching ClassSessionUpdate schema
export interface ClassSessionUpdate {
  class_template_id?: string
  session_id?: string | null
  instructor_id?: string | null
  session_date?: string
  session_start?: string
  session_end?: string
  status?:
    | "scheduled"
    | "in_progress"
    | "completed"
    | "cancelled"
    | "rescheduled"
    | null
  cancellation_reason?: string | null
  location_override?: string | null
  max_students_override?: number | null
  notes?: string | null
}

// Full classSession response matching ClassSessionResponse schema
export interface ClassSessionPublic extends ClassSessionBase {
  id: string
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
}

// Response wrapper for API calls
export interface ClassSessionResponse extends ClassSessionPublic {
  // Enriched fields populated by the backend
  class_name?: string | null
  instructor_name?: string | null
  session_name?: string | null
  location?: string | null
  swim_level?: string | null
}

// Paginated list response matching ClassSessionListResponse schema
export interface ClassSessionListResponse {
  items: ClassSessionResponse[]
  total: number
  page: number
  per_page: number
  pages: number
  meta: ClassSessionListMeta
}

export interface ClassSessionListMeta {
  sort_by: "created_at" | "updated_at"
  sort_order: "asc" | "desc"
  filters: ClassSessionFilters
}

// Form data interfaces for UI components
export interface ClassSessionFormData {
  class_template_id?: string
  session_id?: string | null
  instructor_id?: string | null
  session_date?: string
  session_start?: string
  session_end?: string
  status?:
    | "scheduled"
    | "in_progress"
    | "completed"
    | "cancelled"
    | "rescheduled"
    | null
  cancellation_reason?: string | null
  location_override?: string | null
  max_students_override?: number | null
  notes?: string | null
}

export interface ClassSessionUpdateFormData {
  class_template_id?: string
  session_id?: string | null
  instructor_id?: string | null
  session_date?: string
  session_start?: string
  session_end?: string
  status?:
    | "scheduled"
    | "in_progress"
    | "completed"
    | "cancelled"
    | "rescheduled"
    | null
  cancellation_reason?: string | null
  location_override?: string | null
  max_students_override?: number | null
  notes?: string | null
}

// Filter interface for API queries
export interface ClassSessionFilters {
  limit?: number
  skip?: number
  search?: string
  sort_by?: "created_at" | "updated_at"
  sort_order?: "asc" | "desc"
  created_by?: string
  created_after?: string
  created_before?: string
  updated_after?: string
  updated_before?: string
}

// Component prop interfaces following sophisticated pattern
export interface AddClassSessionProps {
  onSuccess?: () => void
  onCancel?: () => void
}

export interface EditClassSessionProps {
  class_session: ClassSessionResponse
  onSuccess?: () => void
  onCancel?: () => void
}

export interface DeleteClassSessionProps {
  class_session: ClassSessionResponse
  onSuccess?: () => void
  onCancel?: () => void
}

// Zod validation schemas for forms
export const class_sessionCreateSchema = z.object({
  class_template_id: z.string().trim().max(32),
  session_id: z.string().trim().max(32).nullable().optional(),
  instructor_id: z.string().trim().max(32).nullable().optional(),
  session_date: z.string(),
  session_start: z.string(),
  session_end: z.string(),
  status: z
    .enum(["scheduled", "in_progress", "completed", "cancelled", "rescheduled"])
    .nullable()
    .optional(),
  cancellation_reason: z.string().trim().nullable().optional(),
  location_override: z.string().trim().nullable().optional(),
  max_students_override: z.coerce.number().int().nullable().optional(),
  notes: z.string().trim().nullable().optional(),
})

export const class_sessionUpdateSchema = z.object({
  class_template_id: z.string().trim().max(32).optional(),
  session_id: z.string().trim().max(32).nullable().optional(),
  instructor_id: z.string().trim().max(32).nullable().optional(),
  session_date: z.string().optional(),
  session_start: z.string().optional(),
  session_end: z.string().optional(),
  status: z
    .enum(["scheduled", "in_progress", "completed", "cancelled", "rescheduled"])
    .nullable()
    .optional(),
  cancellation_reason: z.string().trim().nullable().optional(),
  location_override: z.string().trim().nullable().optional(),
  max_students_override: z.coerce.number().int().nullable().optional(),
  notes: z.string().trim().nullable().optional(),
})

// Type inference from Zod schemas
export type ClassSessionCreateInput = z.infer<typeof class_sessionCreateSchema>
export type ClassSessionUpdateInput = z.infer<typeof class_sessionUpdateSchema>
